<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<template>
    <Card>
        <CardSection :title="$t('views.storeSettings.general.title')"></CardSection>
        <div class="row" style="padding: 1rem 2rem">
            <div class="col-xs-12 col mb-4">
                <label>{{ $t('views.storeSettings.general.cog') }}</label>
                <select v-model="form.use_usd_on_cog" class="form-select form-select-sm" @change="openModal('use_usd_on_cog', $event.target.value)">
                    <option :value="v" v-for="(k, v) in [getCurrency, 'USD']" :key="v">{{ k }}</option>
                </select>
            </div>
            <div class="col-xs-12 col mb-4">
                <label>{{ $t('views.storeSettings.general.revenue') }}</label>
                <select v-model="form.use_usd_on_revenue" class="form-select form-select-sm" @change="openModal('use_usd_on_revenue', $event.target.value)">
                    <option :value="v" v-for="(k, v) in [getCurrency, 'USD']" :key="v">{{ k }}</option>
                </select>
            </div>
            <div class="col-xs-12 col-lg-3 mb-4">
                <label>{{ $t('views.storeSettings.general.sourceMarket.label') }}</label>
                <multiselect
                    v-model="form.market_source"
                    :placeholder="$t('views.storeSettings.general.sourceMarket.placeholder')"
                    :show-labels="false"
                    :searchable="false"
                    :options="getShortFromMarket"
                    :preselect-first="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                >
                    <template v-slot:singleLabel="{ option }"
                        ><span class="d-flex align-items-center" style="gap: 5px">
                            <img class="option__image" :src="require(`@/assets/svg-flag/${option}.svg`)" /> <span class="d-flex align-items-center">{{ option }}</span>
                        </span></template
                    >
                    <template v-slot:selection="{ values, isOpen }"
                        ><span class="multiselect__single d-flex align-items-center" style="gap: 5px" v-if="values.length" v-show="!isOpen"
                            ><img class="option__image" :src="require(`@/assets/svg-flag/${values[0]}.svg`)" /> <span class="d-flex align-items-center">{{ values[0] }}</span></span
                        ></template
                    >
                    <template v-slot:option="{ option }">
                        <img class="option__image" :src="require(`@/assets/svg-flag/${option}.svg`)" />
                        <div class="option__desc d-flex justify-content-between" style="width: 100%; margin-left: 7px">
                            <span class="option__title">{{ option }}</span>
                            <span v-if="form.market_source === option"><i class="bi-check-lg text-primary"></i></span>
                        </div>
                    </template>
                </multiselect>
            </div>
            <div class="col-xs-12 col-lg-12 mb-4">
                <label>{{ $t('views.storeSettings.general.priceRatio.label') }}</label>
                <div class="d-flex" style="gap: 25px">
                    <div class="input-group" style="width: 200px">
                        <CurrencyInput v-model="form.price_diff_value" :options="{ valueRange: { min: 1, max: undefined } }" :placeholder="$t('views.storeSettings.general.priceRatio.placeholder')" class="sm-input form-control" />
                        <span class="input-group-text">{{ getCurrency }}</span>
                    </div>
                    <select v-model="form.price_diff_option" class="form-select form-select-sm">
                        <option :value="null" disabled>{{ $t('views.storeSettings.select') }}</option>
                        <option v-for="(item, index) in priceDiffOptions" :key="item" :value="item" :disabled="index < 4 && !form.price_diff_value">
                            {{ $t(`views.storeSettings.general.priceRatio.options.${item}`, { price: `${form.price_diff_value || ''} ${form.price_diff_value ? getCurrency : ''}` }) }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-xs-12 col-lg-3 mb-4">
                <label>{{ $t('views.storeSettings.general.expectedMinProfit.label') }}</label>
                <div class="input-group">
                    <CurrencyInput v-model="form.expected_min_profit" :options="{ valueRange: { min: 1, max: undefined } }" :placeholder="$t('views.storeSettings.general.expectedMinProfit.placeholder')" class="sm-input form-control" />
                    <span class="input-group-text">%</span>
                </div>
            </div>
            <div class="col-xs-12 col-lg-3 mb-4">
                <label>{{ $t('views.storeSettings.general.expectedMaxProfit.label') }}</label>
                <div class="input-group">
                    <CurrencyInput v-model="form.expected_max_profit" :options="{ valueRange: { min: 1, max: undefined } }" :placeholder="$t('views.storeSettings.general.expectedMaxProfit.placeholder')" class="sm-input form-control" />
                    <span class="input-group-text">%</span>
                </div>
            </div>
            <div class="col-xs-12 col-lg-6 mb-4">
                <label>{{ $t('views.storeSettings.general.sku.label') }} <InfoTooltip :content="$t('views.storeSettings.general.sku.tooltip')" placement="top" class="text-primary" :opactiy="true"></InfoTooltip> </label>
                <input v-model="form.sku_prefix" type="text" class="form-control" :placeholder="$t('views.storeSettings.general.sku.placeholder')" @keyup="slugify" />
            </div>
            <div class="col-xs-12 col-lg-6 mb-4">
                <label :class="{ 'text-blink-query': $route.query.from === 'downloadLabels' }">{{ $t('views.storeSettings.general.itemLabelLabelType.label') }}</label>
                <select v-model="form.item_label_labelType" class="form-select form-select-sm">
                    <option v-for="value in itemLabelLabelTypes" :key="value" :value="value">{{ $t(`views.storeSettings.general.itemLabelLabelType.options.${value}`) }}</option>
                </select>
            </div>
            <div v-if="form.item_label_labelType === 'THERMAL_PRINTING'" class="col-xs-12 col-lg-6 mb-4">
                <div>
                    <label :class="{ 'text-blink-query': $route.query.from === 'downloadLabels' }">{{ $t('views.storeSettings.general.itemLabelSize.label') }}</label>
                    <div class="d-flex" style="gap: 1rem">
                        <div class="d-flex flex-column">
                            <div class="input-group">
                                <input v-model="form.item_label_width" type="number" :placeholder="$t('views.storeSettings.general.itemLabelSize.width')" class="sm-input form-control" :class="v$.form.item_label_width.$error && 'is-invalid'" />
                                <span class="input-group-text">mm</span>
                            </div>
                            <span v-if="v$.form.item_label_width.$error" class="invalid-feedback">{{ $t('common.required_field') }}</span>
                        </div>

                        <div class="d-flex flex-column">
                            <div class="input-group">
                                <input v-model="form.item_label_height" type="number" :placeholder="$t('views.storeSettings.general.itemLabelSize.height')" class="sm-input form-control" :class="v$.form.item_label_height.$error && 'is-invalid'" />
                                <span class="input-group-text">mm</span>
                            </div>
                            <span v-if="v$.form.item_label_height.$error" class="invalid-feedback">{{ $t('common.required_field') }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="col-xs-12 col-lg-6 mb-4">
                <div>
                    <label>{{ $t('views.storeSettings.general.itemLabelPageType.label') }}</label>
                    <select v-model="form.item_label_pageType" class="form-select form-select-sm">
                        <option :value="null" disabled>{{ $t('views.storeSettings.select') }}</option>
                        <option v-for="value in itemLabelPageTypes" :key="value" :value="value">{{ $t(`views.storeSettings.general.itemLabelPageType.options.${value}`) }}</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end bordered-top mt-1" style="padding: 1rem 2rem 0 0">
            <button class="btn btn-primary" @click="submit">{{ $t('actions.save') }}</button>
        </div>
    </Card>
    <Confirm v-model="isConfirmModalOpen" @selected="confirmModalSelection" />
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { countryInfo } from '@/utils/helper'
import InfoTooltip from '@/components/Variations/InfoTooltip.vue'
import Multiselect from 'vue-multiselect'
import Card from '@/components/Layouts/Card.vue'
import CardSection from '@/components/Layouts/CardSection.vue'
import Confirm from './modals/Confirm.vue'
import CurrencyInput from '@/components/CurrencyInput.vue'
import useVuelidate from '@vuelidate/core'
import { required } from 'vuelidate/lib/validators'

const countries = countryInfo()

export default {
    name: 'StoreSettings',
    components: { InfoTooltip, Multiselect, Card, CardSection, Confirm, CurrencyInput },
    setup() {
        return { v$: useVuelidate() }
    },
    data() {
        return {
            form: {
                use_usd_on_cog: null,
                use_usd_on_revenue: null,
                market_source: null,
                price_diff_option: null,
                price_diff_value: null,
                item_label_labelType: null,
                item_label_pageType: null,
                item_label_width: null,
                item_label_height: null,
                expected_min_profit: null,
                expected_max_profit: null,
                sku_prefix: null,
            },
            priceDiffOptions: [
                'add_inventory_less_than_lowest_seller',
                'add_inventory_more_than_lowest_seller',
                'add_inventory_less_than_buybox_seller',
                'add_inventory_more_than_buybox_seller',
                'add_inventory_equal_to_lowest_seller',
                'add_inventory_equal_to_buybox_seller',
                'add_inventory_with_min_current_cost_calculation',
                'add_inventory_with_max_current_cost_calculation',
            ],
            itemLabelLabelTypes: ['THERMAL_PRINTING', 'STANDARD_FORMAT'],
            itemLabelPageTypes: ['A4_21', 'A4_24', 'A4_24_64x33', 'A4_24_66x35', 'A4_24_70x36', 'A4_24_70x37', 'A4_24i', 'A4_27', 'A4_40_52x29', 'A4_44_48x25', 'Letter_30'],
            isConfirmModalOpen: false,
            modalData: {
                field: null,
                value: null,
            },
        }
    },
    validations() {
        return {
            form: {
                item_label_width: {
                    required,
                    valid: function (val) {
                        if (!val && this.form.item_label_labelType === 'THERMAL_PRINTING') {
                            return false
                        }
                        return true
                    },
                },
                item_label_height: {
                    required,
                    valid: function (val) {
                        if (!val && this.form.item_label_labelType === 'THERMAL_PRINTING') {
                            return false
                        }
                        return true
                    },
                },
            },
        }
    },
    computed: {
        ...mapGetters('market', ['markets', 'currentMarket']),
        ...mapState('settings', ['storeSettings']),
        getCurrency() {
            return countries[this.currentMarket.short].symbol
        },
        getShortFromMarket() {
            const currentMarketShort = this.markets.find((market) => market.short === this.currentMarket.short)
            return currentMarketShort.sources.map((market) => market.source)
        },
    },
    created() {
        this.getStoreSettings(this.currentMarket.idx).then(() => {
            this.form = this.storeSettings
            if (!this.form.item_label_labelType) {
                this.form.item_label_labelType = 'THERMAL_PRINTING'
            }
            if (!this.form.item_label_width) {
                this.form.item_label_width = 25
            }
            if (!this.form.item_label_height) {
                this.form.item_label_height = 50
            }
        })
    },
    methods: {
        ...mapActions('settings', ['getStoreSettings', 'setStoreSettings']),
        async submit() {
            if (this.form.item_label_labelType === 'THERMAL_PRINTING') {
                await this.v$.$validate()
                if (this.v$.$errors.length > 0) return
            }

            this.setStoreSettings({ idx: this.currentMarket.idx, payload: { settings: this.form } })
        },
        slugify(event) {
            const replacedText = event.target.value.replace(/ı/g, 'i').replace(/İ/g, 'i')
            const normalizedText = replacedText.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            this.form.sku_prefix = normalizedText
                .replace(/[^a-zA-Z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .trim('-')
        },
        openModal(field, value) {
            this.modalData = { field, value }
            this.isConfirmModalOpen = true
        },
        confirmModalSelection(confirmed) {
            if (confirmed) {
                this.form[this.modalData.field] = this.modalData.value
            } else {
                this.form[this.modalData.field] = this.form[this.modalData.field] == 1 ? 0 : 1
            }
            this.isConfirmModalOpen = false
        },
    },
    watch: {
        'form.price_diff_value': function (value) {
            if (!value) {
                this.form.price_diff_option = null
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.store-settings-page {
    margin-top: 30px;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 20px;
        border-bottom: 1px solid #e7eaf3;
        margin-bottom: 30px;
    }

    h1 {
        font-size: 22px !important;
        font-weight: 500 !important;
        line-height: 21px !important;
        color: #00203f;
    }

    &__title {
        font-size: 15px;
        font-weight: 500;
        line-height: 21px;
        color: #00203f;
        border-bottom: 1px solid #e7eaf3b2;
        padding-bottom: 20px;
    }

    .card-body {
        padding: 2rem;
    }

    label {
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        color: #00203f;
        margin-bottom: 10px;
    }

    .table-bordered {
        border-left: transparent;

        thead {
            tr {
                height: 48px;

                th:not(:first-child) {
                    text-align: center;
                }
            }
        }

        tbody {
            tr {
                height: 46px;

                td {
                    width: 100px;

                    &:first-child {
                        min-width: 440px;
                    }

                    &:last-child {
                        border-right: transparent;
                    }

                    &:not(:first-child) {
                        text-align: center;
                    }
                }
            }
        }
    }

    .sku-title {
        min-width: 408px;
        max-width: 408px;
        padding-left: 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        vertical-align: middle;
    }

    .secondary-primary {
        color: #fff !important;
        background-color: var(--bs-gray-700) !important;

        &:hover {
            background-color: var(--fba-primary) !important;
        }
    }

    select,
    .form-control {
        height: 41px !important;

        &::placeholder {
            font-size: 13px;
        }
    }

    .input-group-text {
        background-color: #fff !important;
        font-size: 13px;
    }

    .form-check-label {
        font-size: 13px;
        color: #677788;
    }

    .form-check-input {
        &:not(:checked):focus {
            border: 1px solid #11214399;
        }
    }
    .multiselect {
        min-width: 100% !important;
    }
    @keyframes textBlinkQuery {
        0%,
        100% {
            color: #00203f;
        }
        50% {
            color: var(--fba-danger);
        }
    }
    .text-blink-query {
        animation: textBlinkQuery 1s 5;
    }
}
@media (width <= 767px) {
    .store-settings-page .table-bordered tbody tr td:first-child {
        min-width: auto !important;
    }
    table {
        margin-left: -1.5rem !important;

        th,
        td {
            font-size: 12px;
        }
    }
}
@media (768px <= width <= 1024px) {
    .store-settings-page .table-bordered tbody tr td:first-child {
        min-width: auto !important;
    }
}
@media (1025px <= width <= 1366px) {
}
</style>
